import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  collection,
  getDocs,
  getDoc,
  query,
  where,
  addDoc,
  deleteDoc,
  doc,
  Timestamp,
  orderBy,
} from "firebase/firestore";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DATABASE } from "../../../Config/firebase";
import { toast } from "react-toastify";
import TopHeader from "../../../Components/TopHeader/TopHeader";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { dolorFormatAmount } from "../../../Utils/Services";
import { getFundRules } from "../../../Utils/fundRules";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

dayjs.extend(utc);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const YieldMonthlyProcessor = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [processingData, setProcessingData] = useState(null);
  const [submittedRecords, setSubmittedRecords] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyPageSize, setHistoryPageSize] = useState(50);
  const [historySearch, setHistorySearch] = useState("");
  const [historySortBy, setHistorySortBy] = useState("date_desc"); // date_desc, date_asc, name_asc
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedParticipants, setExpandedParticipants] = useState(new Set());
  const [showDailyDetailsDialog, setShowDailyDetailsDialog] = useState(false);
  const [selectedParticipantDetails, setSelectedParticipantDetails] = useState(null);

  // Fetch active products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Fetch all products and filter/sort in memory to avoid index requirement
        const snapshot = await getDocs(collection(DATABASE, "yields_products"));
        const data = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((product) => product.active === true)
          .sort((a, b) => {
            const aTime = a.created_at?.toMillis() || 0;
            const bTime = b.created_at?.toMillis() || 0;
            return bTime - aTime;
          });
        setProducts(data);
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error("Failed to load yield products");
      }
    };

    fetchProducts();
    loadAllSubmittedRecords(null); // Load all records on mount
  }, []);

  // Formik validation schema
  const validationSchema = Yup.object({
    product_id: Yup.string().required("Product is required"),
    month: Yup.string().required("Month is required"),
    year: Yup.string().required("Year is required"),
    default_percentage: Yup.number()
      .required("Default percentage is required")
      .min(0, "Percentage cannot be negative")
      .max(100, "Percentage cannot exceed 100"),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      product_id: "",
      month: dayjs().format("MM"),
      year: dayjs().format("YYYY"),
      default_percentage: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      await handleCalculate(values);
    },
  });

  // Load ALL submitted monthly records (for history table)
  const loadAllSubmittedRecords = async (productId = null) => {
    setLoadingHistory(true);
    try {
      let monthlyQuery;
      if (productId) {
        monthlyQuery = query(
          collection(DATABASE, "yields_monthly"),
          where("product_id", "==", productId),
          orderBy("processed_at", "desc")
        );
      } else {
        monthlyQuery = query(
          collection(DATABASE, "yields_monthly"),
          orderBy("processed_at", "desc")
        );
      }

      const monthlySnapshot = await getDocs(monthlyQuery);
      const records = monthlySnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      // Enrich participants with user_type from users collection (for records saved before user_type was stored)
      const enrichedRecords = await Promise.all(
        records.map(async (record) => {
          const participants = record.participants || [];
          const needsEnrichment = participants.some((p) => p.user_type === undefined);
          if (!needsEnrichment || participants.length === 0) return record;

          const userIds = [...new Set(participants.map((p) => p.user_id).filter(Boolean))];
          const userTypeMap = {};
          await Promise.all(
            userIds.map(async (uid) => {
              try {
                const userSnap = await getDoc(doc(DATABASE, "users", uid));
                userTypeMap[uid] = userSnap.exists() ? userSnap.data().user_type : undefined;
              } catch {
                userTypeMap[uid] = undefined;
              }
            })
          );

          return {
            ...record,
            participants: participants.map((p) => ({
              ...p,
              user_type: p.user_type !== undefined ? p.user_type : userTypeMap[p.user_id],
            })),
          };
        })
      );

      setSubmittedRecords(enrichedRecords);
    } catch (error) {
      console.error("Error loading submitted records:", error);
      toast.error("Failed to load submitted records");
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load submitted records for specific product/month/year (for protection check)
  const loadSubmittedRecordsForMonth = async (productId, month, year) => {
    if (!productId || !month || !year) {
      return [];
    }

    try {
      const monthlyQuery = query(
        collection(DATABASE, "yields_monthly"),
        where("product_id", "==", productId),
        where("month", "==", parseInt(month)),
        where("year", "==", parseInt(year))
      );

      const monthlySnapshot = await getDocs(monthlyQuery);
      return monthlySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error loading submitted records for month:", error);
      return [];
    }
  };

  // ============================================================================
  // DAY-BY-DAY YIELD CALCULATION (Same logic as Performance)
  // ============================================================================
  
  /**
   * Calculate yield for a user day-by-day, applying D0/D+1 logic for deposits/withdrawals
   * This matches the Performance calculation logic exactly
   * 
   * @param {Object} user - User document data
   * @param {string} productId - Yield product ID (user's yield_active_product_id)
   * @param {number} month - Month (1-12)
   * @param {number} year - Year (e.g., 2025)
   * @param {number} monthlyPercentage - Monthly percentage (e.g., 0.3333)
   * @param {string[]} productIdsToMatch - Optional. Both product.id and product.product_id for filtering toggle history
   * @returns {Object} - { grossYieldAmount, daysParticipated, openingBalance, closingBalance, dailyDetails }
   */
  const calculateYieldDayByDay = async (user, productId, month, year, monthlyPercentage, productIdsToMatch = null) => {
    const userId = user.id;
    const monthStart = dayjs(`${year}-${String(month).padStart(2, '0')}-01`).startOf('month');
    const monthEnd = monthStart.endOf('month');
    const daysInMonth = monthStart.daysInMonth();
    // UTC boundaries for ledger comparisons (Firestore timestamps are UTC)
    const monthStartUTC = dayjs.utc(`${year}-${String(month).padStart(2, '0')}-01`).startOf('month');
    const monthEndUTC = monthStartUTC.endOf('month');
    const dailyPercentage = monthlyPercentage / daysInMonth;
    
    console.log(`\n📊 Calculating yield for: ${user.full_name || user.name || userId}`);
    console.log(`   Month: ${monthStart.format('MMM YYYY')}, Days: ${daysInMonth}`);
    console.log(`   Monthly %: ${monthlyPercentage}%, Daily %: ${dailyPercentage.toFixed(6)}%`);
    
    // -------------------------------------------------------------------------
    // STEP 0: Fund closing time (balance for day N = balance at end of day N-1)
    // -------------------------------------------------------------------------
    const fundRules = await getFundRules();
    const cutoffHourUtc = fundRules?.deposit_rules?.cutoff_hour_utc ?? 21;
    console.log(`   📅 Balance rule: last ledger before ${cutoffHourUtc}:00 UTC (fund closing) of previous day`);
    
    // -------------------------------------------------------------------------
    // STEP 1: Fetch user's ledgers (use conclusion_date when available for correct order)
    // -------------------------------------------------------------------------
    const ledgersQuery = query(
      collection(DATABASE, "ledgers"),
      where("user_id", "==", userId),
      orderBy("date", "asc")
    );
    const ledgersSnapshot = await getDocs(ledgersQuery);
    // Use conclusion_date when available (when balance was actually updated), else date
    // CRITICAL: Performance (transection_type 3) is INCLUDED - like Deposit, it ADDS money to the account
    // Fetch conclusion_date from transaction when ledger has doc_id but no conclusion_date (align with ViewLedgers)
    const rawLedgers = ledgersSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
    
    const enrichedLedgers = await Promise.all(rawLedgers.map(async (data) => {
      let dateRaw = data.conclusion_date || data.date;
      // For Deposit/Withdrawal: use conclusion_date (when balance actually changed)
      // CRITICAL: Must use request_approval_time - withdrawals are effective when PAID, not when requested
      if (data.doc_id && (data.transection_type === 0 || data.transection_type === 2)) {
        try {
          const txnRef = doc(DATABASE, "transection_requests", data.doc_id);
          const txnSnap = await getDoc(txnRef);
          if (txnSnap.exists()) {
            const txn = txnSnap.data();
            dateRaw = txn.request_approval_time || txn.date || dateRaw;
          }
        } catch (_) { /* ignore */ }
      }
      const entryDate = dateRaw instanceof Timestamp 
        ? dateRaw.toDate() 
        : data.created_at?.toDate?.() || (dateRaw ? new Date(dateRaw) : null);
      if (!entryDate) return null;
      const d = dayjs.utc(entryDate);
      const typeLabel = data.transection_type === 0 ? 'Deposit' : data.transection_type === 2 ? 'Withdrawal' : data.transection_type === 3 ? 'Performance' : 'Other';
      return d.isValid() ? {
        ...data,
        id: data.id,
        __entryDate: d,
        __typeLabel: typeLabel,
      } : null;
    }));
    
    const allLedgers = enrichedLedgers
      .filter(Boolean)
      .sort((a, b) => a.__entryDate.valueOf() - b.__entryDate.valueOf());
    
    // Use ALL ledgers - Deposit (0), Withdrawal (2), Performance (3) - each changes balance
    console.log(`   Total ledgers: ${allLedgers.length} (incl. Performance)`);
    
    // Debug: Log ledger chain for the month (helps compare with ViewLedgers UI)
    const ledgersInMonthForLog = allLedgers.filter(
      (l) => !l.__entryDate.isBefore(monthStartUTC) && !l.__entryDate.isAfter(monthEndUTC)
    );
    if (ledgersInMonthForLog.length > 0) {
      console.log(`   📋 Ledger chain in month (UTC):`);
      ledgersInMonthForLog.forEach((l, i) => {
        const bal = parseFloat(l?.current_balance ?? l?.balance ?? 0);
        console.log(`      ${i + 1}. ${l.__entryDate.format('DD/MM/YYYY HH:mm')} ${l.__typeLabel} → $${bal.toFixed(2)}`);
      });
    }
    
    // -------------------------------------------------------------------------
    // STEP 2: Get opening balance from previous month's closing
    // -------------------------------------------------------------------------
    const previousMonth = monthStart.subtract(1, 'month');
    const previousMonthNum = previousMonth.month(); // 0-indexed
    const previousYear = previousMonth.year();
    
    let openingBalance = 0;
    
    // Try monthly_closings first
    const closingsQuery = query(
      collection(DATABASE, "monthly_closings"),
      where("user_id", "==", userId),
      where("closing_month", "==", previousMonthNum),
      where("closing_year", "==", previousYear)
    );
    const closingsSnap = await getDocs(closingsQuery);
    
    if (!closingsSnap.empty) {
      openingBalance = parseFloat(closingsSnap.docs[0].data()?.closing_balance || 0);
      console.log(`   💰 Opening from monthly_closings: $${openingBalance.toFixed(2)}`);
    } else {
      const ledgersBeforeMonth = allLedgers.filter((e) => e.__entryDate.isBefore(monthStartUTC));
      if (ledgersBeforeMonth.length > 0) {
        const lastLedger = ledgersBeforeMonth[ledgersBeforeMonth.length - 1];
        openingBalance = parseFloat(lastLedger?.current_balance ?? lastLedger?.balance ?? 0);
        console.log(`   💰 Opening from last ledger (${lastLedger.__entryDate.format('MMM D')}): $${openingBalance.toFixed(2)}`);
      } else {
        console.log(`   💰 Opening: $0 (new user or no previous ledgers)`);
      }
    }
    
    // -------------------------------------------------------------------------
    // STEP 3: Get active days from toggle history
    // PRIMARY: user.yield_toggle_history (array - where Flutter app stores)
    // FALLBACK: yield_toggle_history collection
    // D+1 rule: Toggle ON day X = active from day X+1 (not day X)
    // -------------------------------------------------------------------------
    let toggleHistory = [];
    const idsToMatch = (productIdsToMatch && productIdsToMatch.length > 0) ? productIdsToMatch : [productId];

    // Build from user.yield_toggle_history (Flutter stores here)
    const userToggleArray = user.yield_toggle_history || [];
    let filteredByProduct = userToggleArray.filter(
      (entry) => entry && idsToMatch.includes(entry.product_id || entry.productId)
    );
    // Fallback: if no entries match product ID (e.g. user migrated from old product), use ALL entries
    // User is in this product now - their toggle history is relevant regardless of stored product_id
    if (filteredByProduct.length === 0 && userToggleArray.length > 0) {
      filteredByProduct = userToggleArray.filter((entry) => !!entry);
    }
    const rawFromUser = filteredByProduct
      .map((entry) => {
        const toggleType = entry.toggle_type || (entry.action === 'on' ? 'on' : entry.action === 'off' ? 'off' : null);
        let toggleDate = null;
        if (entry.timestamp !== undefined && entry.timestamp !== null) {
          const ts = typeof entry.timestamp === 'number' ? entry.timestamp : (entry.timestamp?.toMillis?.() ?? entry.timestamp);
          toggleDate = dayjs(ts);
        } else if (entry.toggle_date) {
          toggleDate = entry.toggle_date instanceof Timestamp ? dayjs(entry.toggle_date.toDate()) : dayjs(entry.toggle_date);
        } else if (entry.date) {
          toggleDate = dayjs(entry.date);
        }
        return toggleDate && toggleType ? { ...entry, toggleDate, toggle_type: toggleType } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.toggleDate.valueOf() - b.toggleDate.valueOf());

    toggleHistory = rawFromUser;

    if (toggleHistory.length === 0) {
      try {
        const toggleHistoryQuery = query(
          collection(DATABASE, "yield_toggle_history"),
          where("user_id", "==", userId),
          where("product_id", "==", productId)
        );
        const toggleHistorySnap = await getDocs(toggleHistoryQuery);
        toggleHistory = toggleHistorySnap.docs.map((docSnap) => {
          const data = docSnap.data();
          const toggleType = data.toggle_type || (data.action === 'on' ? 'on' : data.action === 'off' ? 'off' : null);
          const toggleDate = data.toggle_date instanceof Timestamp
            ? dayjs(data.toggle_date.toDate())
            : (data.timestamp !== undefined && data.timestamp !== null
              ? dayjs(typeof data.timestamp === 'number' ? data.timestamp : (data.timestamp?.toMillis?.() ?? data.timestamp))
              : dayjs(data.date));
          return { ...data, toggleDate, toggle_type: toggleType };
        })
          .filter((t) => t.toggleDate && t.toggle_type)
          .sort((a, b) => a.toggleDate.valueOf() - b.toggleDate.valueOf());
      } catch (toggleError) {
        console.log(`   ⚠️ Could not fetch toggle history collection:`, toggleError.message);
      }
    }

    console.log(`   📅 Toggle history entries: ${toggleHistory.length}`);

    const activeDays = new Set();
    let isCurrentlyActive = false;
    let lastToggleBeforeMonth = null;

    for (const toggle of toggleHistory) {
      if (toggle.toggleDate.isBefore(monthStart)) {
        lastToggleBeforeMonth = toggle;
      }
    }
    if (lastToggleBeforeMonth) {
      isCurrentlyActive = lastToggleBeforeMonth.toggle_type === 'on';
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = monthStart.date(day);
      const togglesOnThisDay = toggleHistory.filter((t) => t.toggleDate.isSame(currentDate, 'day'));

      let hadToggleOnToday = false;
      for (const toggle of togglesOnThisDay) {
        if (toggle.toggle_type === 'on') {
          isCurrentlyActive = true;
          hadToggleOnToday = true; // D+1: effect starts tomorrow
        } else if (toggle.toggle_type === 'off') {
          isCurrentlyActive = false;
        }
      }

      if (isCurrentlyActive && !hadToggleOnToday) {
        activeDays.add(day);
      }
    }

    console.log(`   📅 Active days from toggle history: ${activeDays.size} of ${daysInMonth}`);
    
    // If no toggle history found, fallback to yield_days_participated
    // (This can be set by Yield Testing Tool for retroactive testing)
    let daysParticipated = activeDays.size;
    if (daysParticipated === 0 && user.yield_days_participated?.[productId] > 0) {
      // Use yield_days_participated as fallback (Testing Tool sets this)
      daysParticipated = user.yield_days_participated[productId];
      for (let day = 1; day <= Math.min(daysParticipated, daysInMonth); day++) {
        activeDays.add(day);
      }
      console.log(`   ⚠️ No toggle history, using yield_days_participated: ${daysParticipated} days`);
    }
    
    // -------------------------------------------------------------------------
    // STEP 3.5: Balance for day N = last ledger before END of day N (fund closing 21:00 UTC)
    // This matches the ledger: the balance shown is what was in effect at fund closing that day.
    // Yield performs on this balance (aligned with what the client sees in their ledger).
    // -------------------------------------------------------------------------
    const getBalanceAtStartOfDay = (day, debugInfo = null) => {
      const endOfDayN = dayjs.utc(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`)
        .hour(cutoffHourUtc).minute(0).second(0).millisecond(0);
      const ledgersBefore = allLedgers.filter((l) => l.__entryDate.isBefore(endOfDayN));
      if (ledgersBefore.length > 0) {
        const lastLedger = ledgersBefore[ledgersBefore.length - 1];
        const bal = parseFloat(lastLedger?.current_balance ?? lastLedger?.balance ?? 0);
        const source = `${lastLedger.__entryDate.format('DD/MM HH:mm')} ${lastLedger.__typeLabel}`;
        if (debugInfo && day <= 20) {
          debugInfo.push({
            day,
            ledgerDate: lastLedger.__entryDate.format('DD/MM/YYYY HH:mm'),
            ledgerType: lastLedger.__typeLabel,
            balance: bal,
            source,
          });
        }
        return { balance: bal, source };
      }
      if (debugInfo && day <= 20) {
        debugInfo.push({ day, ledgerDate: 'opening', ledgerType: '-', balance: openingBalance, source: 'opening' });
      }
      return { balance: openingBalance, source: 'opening' };
    };
    
    // -------------------------------------------------------------------------
    // STEP 4: Day-by-day - balance = last ledger before fund closing (21:00 UTC) of that day
    // All transactions use conclusion_date (when balance actually changed)
    // -------------------------------------------------------------------------
    let totalGrossYield = 0;
    const dailyDetails = [];
    const balanceDebug = [];
    
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = monthStart.date(day);
      const isActiveToday = activeDays.has(day);
      const { balance: balanceAtStart, source: balanceSource } = getBalanceAtStartOfDay(day, balanceDebug);
      
      if (isActiveToday && day <= 5) {
        console.log(`      Day ${day}: Balance at start (from ledger): $${balanceAtStart.toFixed(2)} [${balanceSource}]`);
      }
      
      let dailyYield = 0;
      if (isActiveToday && balanceAtStart > 0) {
        dailyYield = (balanceAtStart * dailyPercentage) / 100;
        totalGrossYield += dailyYield;
      }
      
      dailyDetails.push({
        day,
        date: currentDate.format('YYYY-MM-DD'),
        isActive: isActiveToday,
        balanceBeforeDeposit: balanceAtStart,
        balanceSource, // For UI: which ledger provided this balance (compare with ViewLedgers)
        dailyYield,
      });
    }
    
    // Closing balance = last ledger in month (use UTC for consistency)
    const ledgersInMonth = allLedgers.filter(
      (l) => !l.__entryDate.isBefore(monthStartUTC) && !l.__entryDate.isAfter(monthEndUTC)
    );
    let closingBalance = openingBalance;
    if (ledgersInMonth.length > 0) {
      const lastLedgerInMonth = ledgersInMonth[ledgersInMonth.length - 1];
      closingBalance = parseFloat(lastLedgerInMonth?.current_balance ?? lastLedgerInMonth?.balance ?? openingBalance);
    } else {
      const depositsInMonth = allLedgers.filter(
        (e) => e.transection_type === 0 && !e.__entryDate.isBefore(monthStartUTC) && !e.__entryDate.isAfter(monthEndUTC)
      );
      const withdrawalsInMonth = allLedgers.filter(
        (e) => e.transection_type === 2 && !e.__entryDate.isBefore(monthStartUTC) && !e.__entryDate.isAfter(monthEndUTC)
      );
      closingBalance = openingBalance
        + depositsInMonth.reduce((s, d) => s + parseFloat(d.new_txn_amount || d.amount || 0), 0)
        - withdrawalsInMonth.reduce((s, w) => s + parseFloat(w.new_txn_amount || w.amount || 0), 0);
    }
    
    console.log(`   📈 Total Gross Yield: $${totalGrossYield.toFixed(2)} over ${daysParticipated} active days`);
    
    // Debug: Day-by-day balance source (compare with ViewLedgers)
    if (balanceDebug.length > 0) {
      console.log(`   📋 Day-by-day balance source (compare with ledger):`);
      balanceDebug.forEach((d) => {
        console.log(`      Day ${String(d.day).padStart(2)}: ${d.ledgerDate} ${d.ledgerType} → $${d.balance.toFixed(2)}`);
      });
    }
    
    return {
      grossYieldAmount: totalGrossYield,
      daysParticipated: activeDays.size,
      openingBalance,
      closingBalance,
      dailyDetails,
    };
  };

  // Calculate monthly yield for participants
  const handleCalculate = async (values) => {
    setIsLoading(true);
    try {
      // Find selected product
      const product = products.find((p) => p.id === values.product_id);
      if (!product) {
        toast.error("Product not found");
        setIsLoading(false);
        return;
      }

      // Validate that selected month/year is within product period
      const month = parseInt(values.month);
      const year = parseInt(values.year);
      const selectedDate = dayjs(`${year}-${month.toString().padStart(2, '0')}-01`); // First day of selected month
      const selectedMonthStart = selectedDate.startOf('month');
      const selectedMonthEnd = selectedDate.endOf('month');
      
      console.log(`🔍 Validating period for product: ${product.product_name}`);
      console.log(`   Selected month: ${month}/${year} (${selectedMonthStart.format('DD/MM/YYYY')} to ${selectedMonthEnd.format('DD/MM/YYYY')})`);
      
      // Get product period dates
      let productStartDate = null;
      let productEndDate = null;
      
      if (product.start_date) {
        productStartDate = product.start_date.toDate ? dayjs(product.start_date.toDate()) : dayjs(product.start_date);
        console.log(`   Product start date: ${productStartDate.format('DD/MM/YYYY')}`);
      }
      
      if (product.end_date) {
        productEndDate = product.end_date.toDate ? dayjs(product.end_date.toDate()) : dayjs(product.end_date);
        console.log(`   Product end date: ${productEndDate.format('DD/MM/YYYY')}`);
      }
      
      // VALIDATION: Check if selected month is completely outside product period
      // Block if:
      // 1. Selected month starts after product end date, OR
      // 2. Selected month ends before product start date
      
      if (productEndDate && selectedMonthStart.isAfter(productEndDate, 'day')) {
        console.log(`   ❌ BLOCKED: Selected month (${selectedMonthStart.format('DD/MM/YYYY')}) starts AFTER product end date (${productEndDate.format('DD/MM/YYYY')})`);
        toast.error(`Product was not available on the dates selected. Product period: ${productStartDate ? productStartDate.format('MM/YYYY') : 'N/A'} to ${productEndDate.format('MM/YYYY')}`);
        setIsLoading(false);
        return;
      }
      
      if (productStartDate && selectedMonthEnd.isBefore(productStartDate, 'day')) {
        console.log(`   ❌ BLOCKED: Selected month (${selectedMonthEnd.format('DD/MM/YYYY')}) ends BEFORE product start date (${productStartDate.format('DD/MM/YYYY')})`);
        toast.error(`Product was not available on the dates selected. Product period: ${productStartDate.format('MM/YYYY')} to ${productEndDate ? productEndDate.format('MM/YYYY') : 'N/A'}`);
        setIsLoading(false);
        return;
      }
      
      // Additional check: if month partially overlaps but shouldn't be processed
      // Block if the month extends beyond the product end date
      if (productEndDate && selectedMonthEnd.isAfter(productEndDate, 'day')) {
        console.log(`   ❌ BLOCKED: Selected month (${selectedMonthEnd.format('DD/MM/YYYY')}) extends beyond product end date (${productEndDate.format('DD/MM/YYYY')})`);
        toast.error(`Product was not available on the dates selected. Product period: ${productStartDate ? productStartDate.format('MM/YYYY') : 'N/A'} to ${productEndDate.format('MM/YYYY')}`);
        setIsLoading(false);
        return;
      }
      
      console.log(`   ✅ Month is within product period`);

      setSelectedProduct(product);

      // Load submitted records for this product/month/year (for protection check)
      const monthRecords = await loadSubmittedRecordsForMonth(
        product.product_id,
        values.month,
        values.year
      );

      // CRITICAL: Fetch users matching EITHER Document ID OR product_id field
      // Same fix as YieldProductManagement - some users have document ID (migrated), others have product_id field
      const documentId = product.id;
      const productIdField = product.product_id;

      const activeUsersQuery = query(
        collection(DATABASE, "users"),
        where("yield_active", "==", true)
      );
      const activeUsersSnapshot = await getDocs(activeUsersQuery);

      const users = activeUsersSnapshot.docs
        .filter((docSnap) => {
          const userData = docSnap.data();
          const userProductId = userData.yield_active_product_id;
          return userProductId === documentId || userProductId === productIdField;
        })
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

      // Get list of already processed user IDs from submitted records for THIS month/year
      const processedUserIds = new Set();
      monthRecords.forEach((record) => {
        record.participants?.forEach((participant) => {
          processedUserIds.add(participant.user_id);
        });
      });

      // Get default commission from product
      const defaultCommission = parseFloat(product.default_commission_percentage !== undefined ? product.default_commission_percentage : 30);

      // Calculate yield for each user (excluding already processed)
      const participantsList = [];
      const alreadyProcessedUsers = [];
      
      const monthlyPercentage = parseFloat(values.default_percentage);
      // month and year already declared above
      
      console.log(`\n🎯 Starting Yield Calculation for ${product.product_name}`);
      console.log(`   Month: ${month}/${year}, Monthly %: ${monthlyPercentage}%`);
      console.log(`   Default Commission: ${defaultCommission}%`);
      console.log(`   Users to process: ${users.length}`);
      
      for (const user of users) {
        // Skip if user was already processed for this month
        if (processedUserIds.has(user.id)) {
          alreadyProcessedUsers.push({
            user_id: user.id,
            user_name: user.full_name || user.name || "N/A",
          });
          continue;
        }

        const userProductId = user.yield_active_product_id || product.product_id;
        const idsForToggleMatch = [product.id, product.product_id].filter(Boolean);

        const yieldResult = await calculateYieldDayByDay(
          user,
          userProductId,
          month,
          year,
          monthlyPercentage,
          idsForToggleMatch
        );
        
        const { grossYieldAmount, daysParticipated, openingBalance, closingBalance, dailyDetails } = yieldResult;
        
        // Calculate commission
        const fidduxCommission = (grossYieldAmount * defaultCommission) / 100;
        const netYieldAmount = grossYieldAmount - fidduxCommission;

        participantsList.push({
          user_id: user.id,
          user_name: user.full_name || user.name || "N/A",
          user_type: user.user_type, // 0 = Individual, 1 = Corporate
          net_balance: closingBalance, // Current balance (after all transactions)
          opening_balance: openingBalance,
          closing_balance: closingBalance,
          days_participated: daysParticipated,
          monthly_percentage: monthlyPercentage,
          custom_percentage: monthlyPercentage, // Can be edited by admin
          custom_commission_percentage: defaultCommission, // Can be edited by admin
          gross_yield_amount: grossYieldAmount,
          fiddux_commission: fidduxCommission,
          net_yield_amount: netYieldAmount,
          dailyDetails: dailyDetails, // For visualization
        });
      }

      participantsList.sort((a, b) => (a.user_name || '').localeCompare(b.user_name || '', undefined, { sensitivity: 'base' }));
      setParticipants(participantsList);
      setIsLoading(false);

      if (alreadyProcessedUsers.length > 0) {
        toast.warning(
          `${alreadyProcessedUsers.length} user(s) already processed for this month. They are excluded from the calculation.`
        );
      }

      if (participantsList.length === 0) {
        if (alreadyProcessedUsers.length > 0) {
          toast.info("All active participants have already been processed for this month.");
        } else {
          toast.info("No active participants found for this product");
        }
      }
    } catch (error) {
      console.error("Error calculating yield:", error);
      toast.error("Failed to calculate yield: " + error.message);
      setIsLoading(false);
    }
  };

  // Handle custom percentage change (memoized to prevent child re-renders)
  // NOTE: When admin overrides percentage, we use a simplified calculation based on closing balance.
  // This is an approximation for quick admin adjustments. The initial calculation uses full day-by-day logic.
  const handleCustomPercentageChange = useCallback((userId, newPercentage) => {
    setParticipants((prev) =>
      prev.map((p) => {
        if (p.user_id === userId) {
          // Use average of opening and closing balance for more accurate approximation
          const avgBalance = (p.opening_balance + p.net_balance) / 2 || p.net_balance;
          const daysInMonth = 30; // Approximation
          const dailyPercentage = parseFloat(newPercentage) / daysInMonth;
          const grossYieldAmount =
            (avgBalance * dailyPercentage * p.days_participated) / 100;
          const fidduxCommission = (grossYieldAmount * p.custom_commission_percentage) / 100;
          const netYieldAmount = grossYieldAmount - fidduxCommission;
          return {
            ...p,
            custom_percentage: parseFloat(newPercentage),
            gross_yield_amount: grossYieldAmount,
            fiddux_commission: fidduxCommission,
            net_yield_amount: netYieldAmount,
          };
        }
        return p;
      })
    );
  }, []); // Empty deps - function doesn't depend on external values

  // Handle custom commission percentage change (memoized)
  const handleCustomCommissionChange = useCallback((userId, newCommission) => {
    setParticipants((prev) =>
      prev.map((p) => {
        if (p.user_id === userId) {
          const fidduxCommission = (p.gross_yield_amount * parseFloat(newCommission)) / 100;
          const netYieldAmount = p.gross_yield_amount - fidduxCommission;
          return {
            ...p,
            custom_commission_percentage: parseFloat(newCommission),
            fiddux_commission: fidduxCommission,
            net_yield_amount: netYieldAmount,
          };
        }
        return p;
      })
    );
  }, []); // Empty deps - function doesn't depend on external values

  // Handle submit monthly yield
  const handleSubmitMonthly = () => {
    if (participants.length === 0) {
      toast.warning("No participants to process");
      return;
    }

    setProcessingData({
      product_id: selectedProduct.product_id,
      product_name: selectedProduct.product_name,
      month: formik.values.month,
      year: formik.values.year,
      participants,
    });
    setShowConfirmDialog(true);
  };

  // Confirm and process monthly yield
  const confirmProcessMonthly = async () => {
    setShowConfirmDialog(false);
    setIsProcessing(true);

    try {
      const { product_id, product_name, month, year, participants } = processingData;

      // Create monthly record in yields_monthly
      const monthlyRecord = {
        product_id,
        product_name,
        month: parseInt(month),
        year: parseInt(year),
        total_participants: participants.length,
        total_gross_yield: participants.reduce((sum, p) => sum + (p.gross_yield_amount || 0), 0),
        total_commission: participants.reduce((sum, p) => sum + (p.fiddux_commission || 0), 0),
        total_net_yield: participants.reduce((sum, p) => sum + (p.net_yield_amount || 0), 0),
        participants: participants.map((p) => ({
          user_id: p.user_id,
          user_name: p.user_name,
          user_type: p.user_type, // 0 = Individual, 1 = Corporate
          net_balance: p.net_balance,
          opening_balance: p.opening_balance,
          closing_balance: p.closing_balance,
          days_participated: p.days_participated,
          monthly_percentage: p.custom_percentage,
          gross_yield_amount: p.gross_yield_amount,
          fiddux_commission_percentage: p.custom_commission_percentage,
          fiddux_commission: p.fiddux_commission,
          net_yield_amount: p.net_yield_amount,
          dailyDetails: p.dailyDetails || [], // Save day-by-day breakdown for future review
        })),
        processed_at: Timestamp.now(),
        processed_by: JSON.parse(localStorage.getItem("fiddux_info"))?.email || "admin",
      };

      await addDoc(collection(DATABASE, "yields_monthly"), monthlyRecord);

      toast.success(
        `Monthly yield processed successfully for ${participants.length} participants!`
      );

      // Reload all submitted records to show the new submission
      await loadAllSubmittedRecords(null);

      // Reset form and state
      formik.resetForm();
      setParticipants([]);
      setSelectedProduct(null);
      setIsProcessing(false);
    } catch (error) {
      console.error("Error processing monthly yield:", error);
      toast.error("Failed to process monthly yield");
      setIsProcessing(false);
    }
  };

  // Handle delete record
  const handleDeleteRecord = (record) => {
    setRecordToDelete(record);
    setShowDeleteDialog(true);
  };

  // Confirm delete record
  const confirmDeleteRecord = async () => {
    if (!recordToDelete) return;
    
    setIsDeleting(true);
    try {
      await deleteDoc(doc(DATABASE, "yields_monthly", recordToDelete.id));
      toast.success("Record deleted successfully!");
      setShowDeleteDialog(false);
      setRecordToDelete(null);
      // Reload history
      await loadAllSubmittedRecords(null);
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error("Failed to delete record: " + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Calculate totals by user type (Individual = 0, Corporate = 1)
  const { individualNetYield, corporateNetYield, individualCount, corporateCount } = useMemo(() => {
    let indNet = 0;
    let corpNet = 0;
    let indCount = 0;
    let corpCount = 0;
    participants.forEach((p) => {
      const net = p.net_yield_amount || 0;
      if (p.user_type === 1) {
        corpNet += net;
        corpCount++;
      } else {
        indNet += net;
        indCount++;
      }
    });
    return { individualNetYield: indNet, corporateNetYield: corpNet, individualCount: indCount, corporateCount: corpCount };
  }, [participants]);

  // Calculate totals (memoized to prevent recalculation on every render)
  const totalGrossYield = useMemo(() => 
    participants.reduce((sum, p) => sum + p.gross_yield_amount, 0),
    [participants]
  );

  const totalCommission = useMemo(() => 
    participants.reduce((sum, p) => sum + p.fiddux_commission, 0),
    [participants]
  );

  const totalNetYield = useMemo(() => 
    participants.reduce((sum, p) => sum + p.net_yield_amount, 0),
    [participants]
  );

  // Process and filter history records
  const processedHistoryRecords = submittedRecords.map((record) => {
    const totalGross = record.participants?.reduce((sum, p) => sum + (p.gross_yield_amount || 0), 0) || 0;
    const totalCommission = record.participants?.reduce((sum, p) => sum + (p.fiddux_commission || 0), 0) || 0;
    const totalNet = record.participants?.reduce((sum, p) => sum + (p.net_yield_amount || 0), 0) || 0;
    // Individual = user_type !== 1 (Corporate). Records without user_type treated as Individual.
    const individualNetYield = record.participants?.reduce(
      (sum, p) => sum + (p.user_type === 1 ? 0 : (p.net_yield_amount || 0)),
      0
    ) ?? null;

    // Get all participant names
    const participantNames = record.participants?.map(p => p.user_name || "N/A").join(", ") || "No participants";

    return {
      ...record,
      totalGross,
      totalCommission,
      totalNet,
      individualNetYield,
      participantNames,
      processedDate: record.processed_at ? record.processed_at.toDate() : null,
    };
  });

  // Filter history records
  const filteredHistoryRecords = processedHistoryRecords.filter((record) => {
    if (historySearch) {
      const searchLower = historySearch.toLowerCase();
      return (
        record.product_name?.toLowerCase().includes(searchLower) ||
        record.participantNames.toLowerCase().includes(searchLower) ||
        record.month?.toString().includes(searchLower) ||
        record.year?.toString().includes(searchLower)
      );
    }
    return true;
  });

  // Sort history records
  const sortedHistoryRecords = [...filteredHistoryRecords].sort((a, b) => {
    if (historySortBy === "date_desc") {
      if (!a.processedDate && !b.processedDate) return 0;
      if (!a.processedDate) return 1;
      if (!b.processedDate) return -1;
      return b.processedDate.getTime() - a.processedDate.getTime();
    } else if (historySortBy === "date_asc") {
      if (!a.processedDate && !b.processedDate) return 0;
      if (!a.processedDate) return 1;
      if (!b.processedDate) return -1;
      return a.processedDate.getTime() - b.processedDate.getTime();
    } else if (historySortBy === "name_asc") {
      const nameA = a.participantNames.toLowerCase();
      const nameB = b.participantNames.toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      // If names are equal, sort by date desc
      if (!a.processedDate && !b.processedDate) return 0;
      if (!a.processedDate) return 1;
      if (!b.processedDate) return -1;
      return b.processedDate.getTime() - a.processedDate.getTime();
    }
    return 0;
  });

  // Paginate history records
  const paginatedHistoryRecords = sortedHistoryRecords.slice(
    historyPage * historyPageSize,
    (historyPage + 1) * historyPageSize
  );
  const totalHistoryPages = Math.ceil(sortedHistoryRecords.length / historyPageSize);

  return (
    <Box>
      <TopHeader title="Yield Monthly Processor" />

      <Box sx={{ 
        p: { xs: 2, md: 3 }, 
        mt: { xs: 10, md: 8 }, // Mais espaço em mobile para compensar TopHeader fixo
        bgcolor: "background.default", // Garante background escuro
      }}>
        {/* Header Information */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
            How Monthly Processing Works:
          </Typography>
          <Typography variant="body1" component="div" sx={{ fontSize: "1rem", lineHeight: 1.6 }}>
            <strong>Step 1:</strong> Select a Yield product and the month/year to process.
            <br />
            <strong>Step 2:</strong> Enter the default monthly percentage for all participants.
            <br />
            <strong>Step 3:</strong> Click "Calculate" to see all active participants and their yield calculations.
            <br />
            <strong>Step 4:</strong> Review and adjust individual monthly percentages and commission rates if needed.
            <br />
            <strong>Step 5:</strong> Click "Submit Monthly Yield" to save the monthly record.
            <br />
            <br />
            <strong>Note:</strong> Monthly processing calculates Gross Yield, Commission, and Net Yield. 
            Commission is tracked but only deducted at final submission.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          {/* Configuration Card */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Monthly Configuration
                </Typography>

                <form onSubmit={formik.handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Yield Product"
                        name="product_id"
                        value={formik.values.product_id || ""}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.product_id &&
                          Boolean(formik.errors.product_id)
                        }
                        SelectProps={{ 
                          native: true,
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        helperText={
                          formik.touched.product_id && formik.errors.product_id
                        }
                      >
                        <option value="" disabled>
                          Select Product
                        </option>
                        {products.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.product_name}
                          </option>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        select
                        fullWidth
                        label="Month"
                        name="month"
                        value={formik.values.month}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.month && Boolean(formik.errors.month)
                        }
                        helperText={formik.touched.month && formik.errors.month}
                        SelectProps={{ native: true }}
                      >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                          <option key={m} value={String(m).padStart(2, "0")}>
                            {dayjs().month(m - 1).format("MMMM")}
                          </option>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        select
                        fullWidth
                        label="Year"
                        name="year"
                        value={formik.values.year}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.year && Boolean(formik.errors.year)
                        }
                        helperText={formik.touched.year && formik.errors.year}
                        SelectProps={{ native: true }}
                      >
                        {Array.from({ length: 4 }, (_, i) => 2024 + i).map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Default Monthly %"
                        name="default_percentage"
                        value={formik.values.default_percentage}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.default_percentage &&
                          Boolean(formik.errors.default_percentage)
                        }
                        helperText={
                          formik.touched.default_percentage &&
                          formik.errors.default_percentage
                        }
                        inputProps={{ step: "0.01" }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        disabled={isLoading}
                        sx={{
                          background:
                            "linear-gradient(135deg, #2af598 0%, #00ba2d 100%)",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #3adecc 0%, #2af598 100%)",
                          },
                        }}
                      >
                        {isLoading ? <CircularProgress size={24} /> : "Calculate"}
                      </Button>
                    </Grid>
                  </Grid>
                </form>

                {participants.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Total Participants:</strong> {participants.length}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Gross Yield:</strong> {dolorFormatAmount(totalGrossYield)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Commission (Fiddux):</strong> {dolorFormatAmount(totalCommission)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Net Yield (to Users):</strong> {dolorFormatAmount(totalNetYield)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, pt: 1, borderTop: "1px solid rgba(255,255,255,0.2)" }}>
                        <strong>Net Yield by Type:</strong>
                      </Typography>
                      <Typography variant="body2">
                        Individual ({individualCount}): {dolorFormatAmount(individualNetYield)}
                      </Typography>
                      <Typography variant="body2">
                        Corporate ({corporateCount}): {dolorFormatAmount(corporateNetYield)}
                      </Typography>
                    </Alert>

                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      onClick={handleSubmitMonthly}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <CircularProgress size={24} />
                      ) : (
                        "Submit Monthly Yield"
                      )}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Participants Table */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Participants ({participants.length})
                </Typography>

                {participants.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                          <TableCell>User</TableCell>
                          <TableCell align="right">Net Balance</TableCell>
                          <TableCell align="center">Days</TableCell>
                          <TableCell align="center">% Monthly</TableCell>
                          <TableCell align="right">Gross Yield</TableCell>
                          <TableCell align="center">Commission %</TableCell>
                          <TableCell align="right">Commission $</TableCell>
                          <TableCell align="right">Net Yield</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {participants.map((participant) => (
                          <TableRow key={participant.user_id}>
                            <TableCell>{participant.user_name}</TableCell>
                            <TableCell align="right">
                              {dolorFormatAmount(participant.net_balance)}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={participant.days_participated}
                                size="small"
                                color="primary"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={participant.custom_percentage}
                                onChange={(e) =>
                                  handleCustomPercentageChange(
                                    participant.user_id,
                                    e.target.value
                                  )
                                }
                                inputProps={{ step: "0.01", style: { textAlign: "center" } }}
                                sx={{ width: "80px" }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ color: "#666" }}>
                                {dolorFormatAmount(participant.gross_yield_amount)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={participant.custom_commission_percentage}
                                onChange={(e) =>
                                  handleCustomCommissionChange(
                                    participant.user_id,
                                    e.target.value
                                  )
                                }
                                inputProps={{ step: "0.01", style: { textAlign: "center" } }}
                                sx={{ width: "70px" }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ color: "#ff6b6b" }}>
                                {dolorFormatAmount(participant.fiddux_commission)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold", color: "#00ba2d" }}
                              >
                                {dolorFormatAmount(participant.net_yield_amount)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setSelectedParticipantDetails(participant);
                                  setShowDailyDetailsDialog(true);
                                }}
                                disabled={!participant.dailyDetails || participant.dailyDetails.length === 0}
                              >
                                View Details
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">
                    Select a product and click "Calculate" to view participants
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Submitted Records History - Always Visible */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6">
                    Submitted Records History
                  </Typography>
                  <Chip
                    label={`${sortedHistoryRecords.length} record(s)`}
                    color={sortedHistoryRecords.length > 0 ? "success" : "default"}
                    size="small"
                  />
                </Box>

                {/* Filters */}
                <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                  <TextField
                    size="small"
                    placeholder="Search by product, user name, month, year..."
                    value={historySearch}
                    onChange={(e) => {
                      setHistorySearch(e.target.value);
                      setHistoryPage(0);
                    }}
                    sx={{ minWidth: "300px", flex: 1 }}
                  />
                  <FormControl size="small" sx={{ minWidth: "200px" }}>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={historySortBy}
                      label="Sort By"
                      onChange={(e) => {
                        setHistorySortBy(e.target.value);
                        setHistoryPage(0);
                      }}
                    >
                      <MenuItem value="date_desc">Latest First</MenuItem>
                      <MenuItem value="date_asc">Oldest First</MenuItem>
                      <MenuItem value="name_asc">Name (A-Z) + Date</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {loadingHistory ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : sortedHistoryRecords.length === 0 ? (
                  <Alert severity="info">
                    {historySearch ? "No records match your search criteria" : "No records submitted yet"}
                  </Alert>
                ) : (
                  <>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                            <TableCell width="50px"></TableCell>
                            <TableCell>Product</TableCell>
                            <TableCell>Month/Year</TableCell>
                            <TableCell>Submitted Date</TableCell>
                            <TableCell>Participants</TableCell>
                            <TableCell align="right">Individual Net Yield</TableCell>
                            <TableCell align="right">Total Gross Yield</TableCell>
                            <TableCell align="right">Total Commission</TableCell>
                            <TableCell align="right">Total Net Yield</TableCell>
                            <TableCell align="center" width="60px">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedHistoryRecords.map((record) => {
                            const isExpanded = expandedRows.has(record.id);
                            
                            const toggleExpand = () => {
                              setExpandedRows((prev) => {
                                const newSet = new Set(prev);
                                if (newSet.has(record.id)) {
                                  newSet.delete(record.id);
                                } else {
                                  newSet.add(record.id);
                                }
                                return newSet;
                              });
                            };
                            
                            return (
                              <React.Fragment key={record.id}>
                                <TableRow>
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={toggleExpand}
                                    >
                                      {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                                      {record.product_name || "N/A"}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    {record.month && record.year
                                      ? `${dayjs().month(record.month - 1).format("MMM")} ${record.year}`
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    {record.processedDate
                                      ? dayjs(record.processedDate).format("MMM DD, YYYY HH:mm")
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={record.total_participants || record.participants?.length || 0}
                                      size="small"
                                      color="primary"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "#00ba2d" }}>
                                      {record.individualNetYield != null
                                        ? dolorFormatAmount(record.individualNetYield)
                                        : "—"}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ color: "#666" }}>
                                      {dolorFormatAmount(record.totalGross)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ color: "#ff6b6b" }}>
                                      {dolorFormatAmount(record.totalCommission)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "#00ba2d" }}>
                                      {dolorFormatAmount(record.totalNet)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteRecord(record)}
                                      title="Delete this record"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell colSpan={10} sx={{ py: 0, border: 0 }}>
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                      <Box sx={{ margin: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                                          Participants Details:
                                        </Typography>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>User Name</TableCell>
                                              <TableCell align="right">Net Balance</TableCell>
                                              <TableCell align="center">Days</TableCell>
                                              <TableCell align="center">Monthly %</TableCell>
                                              <TableCell align="right">Gross Yield</TableCell>
                                              <TableCell align="right">Commission</TableCell>
                                              <TableCell align="right">Net Yield</TableCell>
                                              <TableCell align="center">Details</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {record.participants?.map((participant, idx) => (
                                              <TableRow key={idx}>
                                                <TableCell>{participant.user_name || "N/A"}</TableCell>
                                                <TableCell align="right">
                                                  {dolorFormatAmount(participant.net_balance || 0)}
                                                </TableCell>
                                                <TableCell align="center">
                                                  <Chip
                                                    label={participant.days_participated || 0}
                                                    size="small"
                                                    color="primary"
                                                  />
                                                </TableCell>
                                                <TableCell align="center">
                                                  <Typography variant="body2">
                                                    {participant.monthly_percentage || participant.custom_percentage || 0}%
                                                  </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                  {dolorFormatAmount(participant.gross_yield_amount || 0)}
                                                </TableCell>
                                                <TableCell align="right">
                                                  {dolorFormatAmount(participant.fiddux_commission || 0)}
                                                </TableCell>
                                                <TableCell align="right">
                                                  <Typography variant="body2" sx={{ fontWeight: "bold", color: "#00ba2d" }}>
                                                    {dolorFormatAmount(participant.net_yield_amount || 0)}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                  {participant.dailyDetails && participant.dailyDetails.length > 0 ? (
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => {
                                                        setSelectedParticipantDetails({
                                                          ...participant,
                                                          custom_percentage: participant.monthly_percentage || participant.custom_percentage,
                                                          custom_commission_percentage: participant.fiddux_commission_percentage,
                                                        });
                                                        setShowDailyDetailsDialog(true);
                                                      }}
                                                      color="primary"
                                                    >
                                                      <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                  ) : (
                                                    <Chip label="N/A" size="small" variant="outlined" />
                                                  )}
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {/* Pagination */}
                    {totalHistoryPages > 1 && (
                      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                        <Pagination
                          count={totalHistoryPages}
                          page={historyPage + 1}
                          onChange={(event, value) => setHistoryPage(value - 1)}
                          color="primary"
                          showFirstButton
                          showLastButton
                        />
                      </Box>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>Confirm Monthly Yield Submission</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to submit the monthly yield for:
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography>
              <strong>Product:</strong> {processingData?.product_name}
            </Typography>
            <Typography>
              <strong>Period:</strong> {processingData?.month}/{processingData?.year}
            </Typography>
            <Typography>
              <strong>Participants:</strong> {processingData?.participants.length}
            </Typography>
            {processingData?.participants && (() => {
              const ind = processingData.participants.filter((p) => p.user_type !== 1);
              const corp = processingData.participants.filter((p) => p.user_type === 1);
              const indNet = ind.reduce((s, p) => s + (p.net_yield_amount || 0), 0);
              const corpNet = corp.reduce((s, p) => s + (p.net_yield_amount || 0), 0);
              return (
                <>
                  <Typography>
                    <strong>Individual ({ind.length}):</strong> {dolorFormatAmount(indNet)}
                  </Typography>
                  <Typography>
                    <strong>Corporate ({corp.length}):</strong> {dolorFormatAmount(corpNet)}
                  </Typography>
                </>
              );
            })()}
            <Typography>
              <strong>Gross Yield:</strong>{" "}
              {dolorFormatAmount(
                processingData?.participants.reduce((sum, p) => sum + p.gross_yield_amount, 0) || 0
              )}
            </Typography>
            <Typography>
              <strong>Commission (Fiddux):</strong>{" "}
              {dolorFormatAmount(
                processingData?.participants.reduce((sum, p) => sum + p.fiddux_commission, 0) || 0
              )}
            </Typography>
            <Typography>
              <strong>Net Yield (to Users):</strong>{" "}
              {dolorFormatAmount(
                processingData?.participants.reduce((sum, p) => sum + p.net_yield_amount, 0) || 0
              )}
            </Typography>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action will record the monthly yield. Make sure all percentages are correct.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={confirmProcessMonthly} variant="contained" color="success">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>⚠️ Delete Monthly Record</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete this record?
          </Typography>
          {recordToDelete && (
            <Box sx={{ mt: 2 }}>
              <Typography>
                <strong>Product:</strong> {recordToDelete.product_name}
              </Typography>
              <Typography>
                <strong>Period:</strong>{" "}
                {recordToDelete.month && recordToDelete.year
                  ? `${dayjs().month(recordToDelete.month - 1).format("MMMM")} ${recordToDelete.year}`
                  : "N/A"}
              </Typography>
              <Typography>
                <strong>Participants:</strong> {recordToDelete.participants?.length || 0}
              </Typography>
              <Typography>
                <strong>Total Net Yield:</strong> {dolorFormatAmount(recordToDelete.totalNet || 0)}
              </Typography>
            </Box>
          )}
          <Alert severity="error" sx={{ mt: 2 }}>
            This action cannot be undone. The record will be permanently deleted.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowDeleteDialog(false);
              setRecordToDelete(null);
            }} 
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteRecord} 
            variant="contained" 
            color="error"
            disabled={isDeleting}
          >
            {isDeleting ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Daily Details Dialog */}
      <Dialog 
        open={showDailyDetailsDialog} 
        onClose={() => {
          setShowDailyDetailsDialog(false);
          setSelectedParticipantDetails(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle component="div">
          <Box>
            <Typography component="span" variant="h6">Day-by-Day Calculation Details</Typography>
            {selectedParticipantDetails && (
              <Typography component="div" variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {selectedParticipantDetails.user_name}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedParticipantDetails && selectedParticipantDetails.dailyDetails ? (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Opening Balance:</strong> {dolorFormatAmount(selectedParticipantDetails.opening_balance || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Closing Balance:</strong> {dolorFormatAmount(selectedParticipantDetails.closing_balance || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Active Days:</strong> {selectedParticipantDetails.days_participated} of {selectedParticipantDetails.dailyDetails.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Monthly %:</strong> {selectedParticipantDetails.custom_percentage}%
                </Typography>
              </Box>

              <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Day</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="center">Active</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell>From Ledger</TableCell>
                      <TableCell align="right">Daily Yield</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedParticipantDetails.dailyDetails.map((detail) => (
                      <TableRow 
                        key={detail.day}
                        sx={{ 
                          backgroundColor: detail.isActive ? "rgba(0, 186, 45, 0.05)" : "transparent" 
                        }}
                      >
                        <TableCell>{detail.day}</TableCell>
                        <TableCell>{dayjs(detail.date).format("DD/MM/YYYY")}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={detail.isActive ? "Yes" : "No"}
                            size="small"
                            color={detail.isActive ? "success" : "default"}
                            sx={{ minWidth: 50 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {dolorFormatAmount(detail.balanceBeforeDeposit)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {detail.balanceSource || '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{ 
                              color: detail.dailyYield > 0 ? "#00ba2d" : "text.secondary",
                              fontWeight: detail.dailyYield > 0 ? "bold" : "normal"
                            }}
                          >
                            {dolorFormatAmount(detail.dailyYield)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                  Summary:
                </Typography>
                <Typography variant="body2">
                  Total Gross Yield: <strong>{dolorFormatAmount(selectedParticipantDetails.gross_yield_amount)}</strong>
                </Typography>
                <Typography variant="body2">
                  Commission ({selectedParticipantDetails.custom_commission_percentage}%): <strong style={{ color: "#ff6b6b" }}>{dolorFormatAmount(selectedParticipantDetails.fiddux_commission)}</strong>
                </Typography>
                <Typography variant="body2">
                  Net Yield: <strong style={{ color: "#00ba2d" }}>{dolorFormatAmount(selectedParticipantDetails.net_yield_amount)}</strong>
                </Typography>
              </Box>
            </>
          ) : (
            <Alert severity="info">No daily details available</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowDailyDetailsDialog(false);
              setSelectedParticipantDetails(null);
            }}
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default YieldMonthlyProcessor;

